import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Navbar } from '../components/layout/Navbar';
import { DocsSidebar } from '../components/layout/DocsSidebar';
import { RelatedDocs } from '../components/RelatedDocs';
import { useAnalyticsTracking } from '../hooks/useAnalyticsTracking';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    ChevronRight, 
    FileText, 
    Clock, 
    Tag, 
    Menu,
    X,
    ArrowLeft,
    ArrowRight,
    Copy,
    Check,
    Download,
    File,
    Languages,
    Cog,
    ThumbsUp,
    ThumbsDown,
    List
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom code block component with syntax highlighting
function CodeBlock({ children, className, ...props }) {
    const [copied, setCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const code = String(children).replace(/\n$/, '');

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!match) {
        return <code className={cn("bg-muted px-1.5 py-0.5 rounded text-sm font-mono", className)} {...props}>{children}</code>;
    }

    return (
        <div className="relative group my-4">
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                <Badge variant="outline" className="text-xs font-mono bg-background/80">
                    {language}
                </Badge>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80"
                    onClick={handleCopy}
                >
                    {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                </Button>
            </div>
            <SyntaxHighlighter
                style={oneDark}
                language={language}
                PreTag="div"
                customStyle={{
                    margin: 0,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
}

export default function DocsPage() {
    const { projectSlug, docSlug } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [docs, setDocs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [docFiles, setDocFiles] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        // Load saved language preference from localStorage
        return localStorage.getItem('docs_language') || 'de';
    });
    const [translations, setTranslations] = useState([]);
    const [configTemplates, setConfigTemplates] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [configValues, setConfigValues] = useState({});
    const [generatedConfig, setGeneratedConfig] = useState('');
    const [showConfigGenerator, setShowConfigGenerator] = useState(false);
    const [feedbackGiven, setFeedbackGiven] = useState(null);
    const [feedbackStats, setFeedbackStats] = useState(null);
    const [showToc, setShowToc] = useState(true);

    // Analytics tracking hook
    useAnalyticsTracking(currentDoc?.id);

    // Parse headings for Table of Contents
    const tableOfContents = useMemo(() => {
        if (!currentDoc?.content) return [];
        
        const headingRegex = /^(#{1,3})\s+(.+)$/gm;
        const headings = [];
        let match;
        
        while ((match = headingRegex.exec(currentDoc.content)) !== null) {
            const level = match[1].length;
            const text = match[2].trim();
            const id = text.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '');
            headings.push({ level, text, id });
        }
        
        return headings;
    }, [currentDoc?.content]);

    useEffect(() => {
        if (projectSlug) {
            fetchProjectData();
        }
    }, [projectSlug]);

    useEffect(() => {
        if (docSlug && project) {
            fetchDoc();
        } else if (docs.length > 0 && !docSlug) {
            // Show first doc if no specific doc is selected
            setCurrentDoc(docs[0]);
        }
    }, [docSlug, project, docs]);

    // Fetch doc files when currentDoc changes
    useEffect(() => {
        if (currentDoc?.id) {
            fetchDocFiles(currentDoc.id);
            fetchTranslations(currentDoc.id, currentDoc.language || 'de');
            fetchFeedbackStats(currentDoc.id);
            setFeedbackGiven(null); // Reset feedback state for new doc
        } else {
            setDocFiles([]);
            setTranslations([]);
            setFeedbackStats(null);
        }
    }, [currentDoc]);

    const fetchDocFiles = async (docId) => {
        try {
            const response = await axios.get(`${API}/docs/${docId}/files`);
            setDocFiles(response.data);
        } catch (error) {
            console.error('Error fetching doc files:', error);
            setDocFiles([]);
        }
    };

    const fetchTranslations = async (docId, currentDocLang) => {
        try {
            const response = await axios.get(`${API}/docs/${docId}/translations`);
            const translationsList = response.data;
            setTranslations(translationsList);

            let preferredLang = 'de';
            try {
                const site = localStorage.getItem('titanv_lang') || '';
                const saved = localStorage.getItem('docs_language') || '';
                preferredLang = (site || saved).startsWith('en') ? 'en' : 'de';
            } catch { /* keep German */ }
            if (preferredLang !== currentDocLang && translationsList.length > 1) {
                const preferredTranslation = translationsList.find(item => item.language === preferredLang);
                if (preferredTranslation && project) {
                    navigate(`/docs/${project.slug}/${preferredTranslation.slug}`, { replace: true });
                }
            }
        } catch (error) {
            console.error('Error fetching translations:', error);
            setTranslations([]);
        }
    };

    const fetchFeedbackStats = async (docId) => {
        try {
            const response = await axios.get(`${API}/docs/${docId}/feedback/stats`);
            setFeedbackStats(response.data);
        } catch (error) {
            console.error('Error fetching feedback stats:', error);
            setFeedbackStats(null);
        }
    };

    const submitFeedback = async (helpful) => {
        if (!currentDoc?.id || feedbackGiven !== null) return;
        
        try {
            await axios.post(`${API}/docs/${currentDoc.id}/feedback?helpful=${helpful}`);
            setFeedbackGiven(helpful);
            // Refresh stats
            fetchFeedbackStats(currentDoc.id);
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    };

    const scrollToHeading = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const switchLanguage = (lang) => {
        try {
            localStorage.setItem('docs_language', lang);
            localStorage.setItem('titanv_lang', lang);
            window.dispatchEvent(new Event('titanv-lang'));
        } catch { /* ignore */ }
        setCurrentLanguage(lang);

        const translation = translations.find(item => item.language === lang);
        if (translation && project && translation.slug !== docSlug) {
            navigate(`/docs/${project.slug}/${translation.slug}`);
        }
    };

    useEffect(() => {
        const sync = () => {
            let lang = 'de';
            try {
                lang = (localStorage.getItem('titanv_lang') || '').startsWith('en') ? 'en' : 'de';
            } catch { return; }
            const translation = translations.find(item => item.language === lang);
            if (translation && project && translation.slug !== docSlug) {
                navigate(`/docs/${project.slug}/${translation.slug}`);
            }
        };
        window.addEventListener('titanv-lang', sync);
        return () => window.removeEventListener('titanv-lang', sync);
    }, [translations, project, docSlug, navigate]);

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const fetchProjectData = async () => {
        try {
            const [projectRes, docsRes, categoriesRes] = await Promise.all([
                axios.get(`${API}/projects/${projectSlug}`),
                axios.get(`${API}/docs?project_id=${projectSlug}`),
                axios.get(`${API}/categories`)
            ]);
            
            setProject(projectRes.data);
            
            // Fetch docs by project ID
            const projectId = projectRes.data.id;
            const docsResponse = await axios.get(`${API}/docs?project_id=${projectId}`);
            setDocs(docsResponse.data);
            
            const projectCategories = categoriesRes.data.filter(c => c.project_id === projectId);
            setCategories(projectCategories);
            
            // Fetch config templates for this project
            const configRes = await axios.get(`${API}/config-templates?project_id=${projectId}`);
            setConfigTemplates(configRes.data);
        } catch (error) {
            console.error('Error fetching project data:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateConfig = async () => {
        if (!selectedConfig) return;
        try {
            const response = await axios.post(`${API}/config-templates/${selectedConfig.id}/generate`, configValues);
            setGeneratedConfig(response.data.config);
        } catch (error) {
            console.error('Error generating config:', error);
        }
    };

    const copyGeneratedConfig = () => {
        navigator.clipboard.writeText(generatedConfig);
    };

    const fetchDoc = async () => {
        const load = (slug) => axios.get(`${API}/docs/by-slug/${projectSlug}/${slug}`);
        try {
            const response = await load(docSlug);
            setCurrentDoc(response.data);
        } catch (error) {
            const missing = error?.response?.status === 404;
            const englishCopy = missing && docSlug.endsWith('-en');
            if (englishCopy) {
                try {
                    const fallback = await load(docSlug.slice(0, -3));
                    setCurrentDoc(fallback.data);
                    navigate(`/docs/${projectSlug}/${docSlug.slice(0, -3)}`, { replace: true });
                    return;
                } catch { /* the German article is missing too */ }
            }
            if (!missing) console.error('Error fetching doc:', error);
            setCurrentDoc(null);
        }
    };

    // Get prev/next docs for navigation
    const sortedDocs = [...docs].sort((a, b) => a.order - b.order);
    const currentIndex = sortedDocs.findIndex(d => d.id === currentDoc?.id);
    const prevDoc = currentIndex > 0 ? sortedDocs[currentIndex - 1] : null;
    const nextDoc = currentIndex < sortedDocs.length - 1 ? sortedDocs[currentIndex + 1] : null;

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-primary font-mono">Loading...</div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <div className="pt-32 text-center">
                    <h1 className="text-2xl font-mono font-bold">Projekt nicht gefunden</h1>
                    <Link to="/" className="text-primary hover:underline mt-4 inline-block">
                        Zurück zur Startseite
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            <Navbar />

            <div className="pt-24 flex">
                {/* Mobile sidebar toggle */}
                <Button
                    variant="outline"
                    size="icon"
                    className="fixed bottom-6 left-6 z-40 lg:hidden rounded-full shadow-lg"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    data-testid="mobile-sidebar-toggle"
                >
                    {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>

                {/* Mobile sidebar overlay */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <div className={cn(
                    "fixed lg:sticky top-24 left-0 h-[calc(100vh-96px)] z-40 lg:z-auto transition-transform",
                    sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                )}>
                    <DocsSidebar 
                        project={project} 
                        docs={docs} 
                        categories={categories}
                    />
                </div>

                {/* Main content */}
                <main className="flex-1 min-w-0 px-6 lg:px-12 py-8">
                    {/* Config Generator Button */}
                    {configTemplates.length > 0 && (
                        <div className="mb-6">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedConfig(configTemplates[0]);
                                    setConfigValues({});
                                    setGeneratedConfig('');
                                    setShowConfigGenerator(true);
                                }}
                                className="w-full sm:w-auto"
                            >
                                <Cog className="w-4 h-4 mr-2" />
                                Config Generator
                            </Button>
                        </div>
                    )}

                    {/* Config Generator Modal */}
                    {showConfigGenerator && selectedConfig && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8 p-6 rounded-lg border border-border bg-card/50"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-mono font-bold flex items-center gap-2">
                                    <Cog className="w-5 h-5 text-primary" />
                                    {selectedConfig.name}
                                </h2>
                                <Button variant="ghost" size="icon" onClick={() => setShowConfigGenerator(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            
                            {configTemplates.length > 1 && (
                                <div className="mb-4">
                                    <select
                                        value={selectedConfig.id}
                                        onChange={e => {
                                            const config = configTemplates.find(c => c.id === e.target.value);
                                            setSelectedConfig(config);
                                            setConfigValues({});
                                            setGeneratedConfig('');
                                        }}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3"
                                    >
                                        {configTemplates.map(c => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {selectedConfig.fields?.map(field => {
                                    // Determine language based on selected config name (EN suffix = English)
                                    const configLang = selectedConfig.name?.includes(' EN') ? 'en' : 'de';
                                    
                                    // Get description in config's language
                                    const fieldDescription = configLang === 'en' 
                                        ? (field.description_en || field.description)
                                        : (field.description_de || field.description);
                                    
                                    return (
                                    <div key={field.name}>
                                        <label className="text-sm font-medium mb-1 block">{field.label}</label>
                                        {fieldDescription && (
                                            <p className="text-xs text-muted-foreground mb-1">{fieldDescription}</p>
                                        )}
                                        {field.type === 'boolean' ? (
                                            <select
                                                value={configValues[field.name] ?? field.default ?? 'false'}
                                                onChange={e => setConfigValues({ ...configValues, [field.name]: e.target.value })}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3"
                                            >
                                                <option value="true">{configLang === 'en' ? 'Yes' : 'Ja'}</option>
                                                <option value="false">{configLang === 'en' ? 'No' : 'Nein'}</option>
                                            </select>
                                        ) : field.type === 'select' ? (
                                            <select
                                                value={configValues[field.name] ?? field.default ?? ''}
                                                onChange={e => setConfigValues({ ...configValues, [field.name]: e.target.value })}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3"
                                            >
                                                {field.options?.map(opt => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type={field.type === 'number' ? 'number' : 'text'}
                                                value={configValues[field.name] ?? field.default ?? ''}
                                                onChange={e => setConfigValues({ ...configValues, [field.name]: e.target.value })}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3"
                                                placeholder={field.default}
                                            />
                                        )}
                                    </div>
                                    );
                                })}
                            </div>

                            <Button onClick={generateConfig} className="mb-4">
                                {selectedConfig?.name?.includes(' EN') ? 'Generate Config' : 'Config generieren'}
                            </Button>

                            {generatedConfig && (
                                <div className="relative">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute top-2 right-2 h-8 w-8"
                                        onClick={copyGeneratedConfig}
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                    <SyntaxHighlighter
                                        style={oneDark}
                                        language="lua"
                                        customStyle={{
                                            margin: 0,
                                            borderRadius: '0.5rem',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {generatedConfig}
                                    </SyntaxHighlighter>
                                </div>
                            )}
                        </motion.div>
                    )}

                    {currentDoc ? (
                        <motion.article
                            key={currentDoc.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                            className="max-w-3xl"
                            data-testid="doc-content"
                        >
                            {/* Breadcrumb */}
                            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                                <Link to="/" className="hover:text-foreground transition-colors">
                                    Home
                                </Link>
                                <ChevronRight className="w-4 h-4" />
                                <Link to={`/docs/${project.slug}`} className="hover:text-foreground transition-colors">
                                    {project.name}
                                </Link>
                                <ChevronRight className="w-4 h-4" />
                                <span className="text-foreground">{currentDoc.title}</span>
                            </nav>

                            {/* Header */}
                            <header className="mb-8">
                                <div className="flex items-center gap-2 mb-4">
                                    <Badge variant="outline" className="font-mono text-xs">
                                        {currentDoc.category}
                                    </Badge>
                                </div>
                                <h1 className="text-4xl font-mono font-bold mb-4">
                                    {currentDoc.title}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        <span>
                                            Aktualisiert: {new Date(currentDoc.updated_at).toLocaleDateString('de-DE')}
                                        </span>
                                    </div>
                                    {currentDoc.tags && currentDoc.tags.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <Tag className="w-4 h-4" />
                                            {currentDoc.tags.map(tag => (
                                                <Badge key={tag} variant="secondary" className="text-xs font-mono">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Language Switcher */}
                                {translations.length > 1 && (
                                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                                        <Languages className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm text-muted-foreground mr-2">Sprache:</span>
                                        {translations.map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => switchLanguage(t.language)}
                                                className={cn(
                                                    "px-2 py-1 rounded text-sm transition-colors",
                                                    (currentDoc.language || 'de') === t.language
                                                        ? "bg-primary text-primary-foreground"
                                                        : "bg-muted hover:bg-muted/80"
                                                )}
                                            >
                                                {t.language === 'de' ? 'DE Deutsch' : 'EN English'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </header>

                            {/* Table of Contents */}
                            {tableOfContents.length > 2 && (
                                <div className="mb-8 p-4 rounded-lg border border-border bg-card/30">
                                    <button
                                        onClick={() => setShowToc(!showToc)}
                                        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground w-full"
                                        data-testid="toc-toggle"
                                    >
                                        <List className="w-4 h-4" />
                                        Inhaltsverzeichnis
                                        <span className="ml-auto text-xs">{showToc ? '▼' : '▶'}</span>
                                    </button>
                                    {showToc && (
                                        <nav className="mt-3 space-y-1" data-testid="table-of-contents">
                                            {tableOfContents.map((heading, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => scrollToHeading(heading.id)}
                                                    className={cn(
                                                        "block text-left text-sm hover:text-primary transition-colors w-full truncate",
                                                        heading.level === 1 && "font-medium text-foreground",
                                                        heading.level === 2 && "pl-4 text-muted-foreground",
                                                        heading.level === 3 && "pl-8 text-muted-foreground text-xs"
                                                    )}
                                                >
                                                    {heading.text}
                                                </button>
                                            ))}
                                        </nav>
                                    )}
                                </div>
                            )}

                            {/* Content */}
                            <div className="prose prose-invert max-w-none">
                                <ReactMarkdown
                                    components={{
                                        code: CodeBlock,
                                        h1: ({ children }) => {
                                            const text = String(children);
                                            const id = text.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '');
                                            return <h1 id={id} className="text-3xl font-mono font-bold mt-8 mb-4 scroll-mt-24">{children}</h1>;
                                        },
                                        h2: ({ children }) => {
                                            const text = String(children);
                                            const id = text.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '');
                                            return <h2 id={id} className="text-2xl font-mono font-bold mt-8 mb-4 pb-2 border-b border-border scroll-mt-24">{children}</h2>;
                                        },
                                        h3: ({ children }) => {
                                            const text = String(children);
                                            const id = text.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '-').replace(/^-|-$/g, '');
                                            return <h3 id={id} className="text-xl font-mono font-semibold mt-6 mb-3 scroll-mt-24">{children}</h3>;
                                        },
                                        p: ({ children }) => (
                                            <p className="text-muted-foreground leading-relaxed mb-4">{children}</p>
                                        ),
                                        ul: ({ children }) => (
                                            <ul className="list-disc list-inside space-y-2 mb-4 text-muted-foreground">{children}</ul>
                                        ),
                                        ol: ({ children }) => (
                                            <ol className="list-decimal list-inside space-y-2 mb-4 text-muted-foreground">{children}</ol>
                                        ),
                                        a: ({ href, children }) => {
                                            // Check if this is a file download link
                                            if (href && href.includes('/api/docs/') && href.includes('/files/')) {
                                                const filename = String(children).replace('📥 ', '');
                                                return (
                                                    <a 
                                                        href={`${process.env.REACT_APP_BACKEND_URL}${href}`}
                                                        download={filename}
                                                        className="inline-flex items-center gap-2 px-3 py-2 my-2 rounded-md bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors font-mono text-sm no-underline"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                        {filename}
                                                    </a>
                                                );
                                            }
                                            return (
                                                <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                                                    {children}
                                                </a>
                                            );
                                        },
                                        blockquote: ({ children }) => (
                                            <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                                                {children}
                                            </blockquote>
                                        ),
                                        img: ({ src, alt }) => (
                                            <img 
                                                src={src} 
                                                alt={alt} 
                                                className="rounded-lg border border-border my-4 max-w-full"
                                            />
                                        ),
                                        table: ({ children }) => (
                                            <div className="overflow-x-auto my-4">
                                                <table className="w-full border-collapse border border-border">
                                                    {children}
                                                </table>
                                            </div>
                                        ),
                                        th: ({ children }) => (
                                            <th className="border border-border bg-muted px-4 py-2 text-left font-mono font-bold">
                                                {children}
                                            </th>
                                        ),
                                        td: ({ children }) => (
                                            <td className="border border-border px-4 py-2">{children}</td>
                                        ),
                                    }}
                                >
                                    {currentDoc.content.replace(/\n/g, '  \n')}
                                </ReactMarkdown>
                            </div>

                            {/* Download Files Section */}
                            {docFiles.length > 0 && (
                                <div className="mt-8 p-6 rounded-lg border border-border bg-card/50" data-testid="doc-files-section">
                                    <h3 className="text-lg font-mono font-bold mb-4 flex items-center gap-2">
                                        <Download className="w-5 h-5 text-primary" />
                                        Downloads
                                    </h3>
                                    <div className="grid gap-2">
                                        {docFiles.map(file => (
                                            <a
                                                key={file.id}
                                                href={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                                                download={file.original_filename}
                                                className="flex items-center justify-between p-3 rounded-md bg-muted/50 hover:bg-muted transition-colors group"
                                                data-testid={`download-file-${file.id}`}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <File className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <span className="font-mono text-sm block truncate">{file.original_filename}</span>
                                                        <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                                                    </div>
                                                </div>
                                                <Download className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Feedback Section */}
                            <div className="mt-8 p-6 rounded-lg border border-border bg-card/30" data-testid="feedback-section">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="text-center sm:text-left">
                                        <h3 className="text-lg font-mono font-medium mb-1">War diese Dokumentation hilfreich?</h3>
                                        {feedbackStats && feedbackStats.total > 0 && (
                                            <p className="text-sm text-muted-foreground">
                                                {feedbackStats.helpful_percent}% fanden das hilfreich ({feedbackStats.total} Bewertungen)
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {feedbackGiven === null ? (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    onClick={() => submitFeedback(true)}
                                                    className="gap-2 hover:bg-green-500/10 hover:border-green-500 hover:text-green-500"
                                                    data-testid="feedback-helpful-btn"
                                                >
                                                    <ThumbsUp className="w-5 h-5" />
                                                    Ja
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="lg"
                                                    onClick={() => submitFeedback(false)}
                                                    className="gap-2 hover:bg-red-500/10 hover:border-red-500 hover:text-red-500"
                                                    data-testid="feedback-not-helpful-btn"
                                                >
                                                    <ThumbsDown className="w-5 h-5" />
                                                    Nein
                                                </Button>
                                            </>
                                        ) : (
                                            <div className="flex items-center gap-2 text-primary">
                                                <Check className="w-5 h-5" />
                                                <span className="font-medium">Danke für dein Feedback!</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Related Docs */}
                            {currentDoc && (
                                <RelatedDocs docId={currentDoc.id} currentTitle={currentDoc.title} />
                            )}

                            {/* Navigation */}
                            <nav className="flex items-center justify-between mt-12 pt-6 border-t border-border">
                                {prevDoc ? (
                                    <Link
                                        to={`/docs/${project.slug}/${prevDoc.slug}`}
                                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
                                        data-testid="prev-doc-link"
                                    >
                                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                        <div>
                                            <div className="text-xs uppercase tracking-wider">Vorherige</div>
                                            <div className="font-mono font-bold">{prevDoc.title}</div>
                                        </div>
                                    </Link>
                                ) : (
                                    <div />
                                )}
                                {nextDoc ? (
                                    <Link
                                        to={`/docs/${project.slug}/${nextDoc.slug}`}
                                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group text-right"
                                        data-testid="next-doc-link"
                                    >
                                        <div>
                                            <div className="text-xs uppercase tracking-wider">Nächste</div>
                                            <div className="font-mono font-bold">{nextDoc.title}</div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                ) : (
                                    <div />
                                )}
                            </nav>
                        </motion.article>
                    ) : docs.length === 0 ? (
                        <div className="text-center py-20">
                            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                            <h2 className="text-2xl font-mono font-bold mb-2">Keine Dokumentation vorhanden</h2>
                            <p className="text-muted-foreground">
                                Dieses Projekt hat noch keine Dokumentationen.
                            </p>
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground">
                                Wähle eine Dokumentation aus der Seitenleiste
                            </p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
